/**
 * Zod schemas for the Design data model and AI-JSON flows.
 *
 * Replaces Anvil's `json5`/`jsonrepair` triple-fallback with `JSON.parse` +
 * zod validation (TDD §7.6). One schema per AI-JSON output lands here as the
 * relevant flow is ported (ScreenSchema, ProposedScreensSchema,
 * HammerPlanSchema, ReviewQuestionsSchema).
 *
 * Phase 0 seeds the structural schemas (used to validate `project.json` and
 * section metadata on read) and confirms `zod` resolves from within
 * crucible-ui (§3.3 package-boundary gate).
 */
import { z } from 'zod';

export const DocKindSchema = z.enum(['gdd', 'prd']);

export const SectionMetaSchema = z.object({
  id: z.string(),
  title: z.string(),
  order: z.number(),
  filename: z.string(),
  doc: DocKindSchema,
  canonical: z.boolean(),
});

export const DesignProjectSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  projectType: z.enum(['gdd', 'prd', 'both']),
  createdAt: z.string(),
  updatedAt: z.string(),
  path: z.string(),
  providers: z.object({
    default: z.string().nullable().optional(),
    models: z.record(z.string(), z.string()),
  }),
  sections: z.array(SectionMetaSchema),
  pillars: z.array(z.string()),
});

export type SectionMetaParsed = z.infer<typeof SectionMetaSchema>;
export type DesignProjectParsed = z.infer<typeof DesignProjectSchema>;

// ---------------------------------------------------------------------------
// Screens — validate AI-generated + on-disk JSON (§7.6).
// ---------------------------------------------------------------------------

export const ZoneSchema = z.object({
  id: z.string(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  label: z.string(),
  kind: z.string(),
  notes: z.string(),
  zIndex: z.number().optional(),
});

/** AI-proposed screens (zones partial — sanitized client-side before save). */
export const ProposedScreensSchema = z.object({
  screens: z
    .array(
      z.object({
        name: z.string(),
        surface: z.enum(['tv', 'phone']),
        description: z.string().default(''),
        zones: z
          .array(
            z.object({
              label: z.string().default(''),
              kind: z.string().default('other'),
              x: z.number().default(0),
              y: z.number().default(0),
              width: z.number().default(100),
              height: z.number().default(60),
              notes: z.string().default(''),
            }),
          )
          .default([]),
      }),
    )
    .default([]),
});

export const ScreenSchema = z.object({
  id: z.string(),
  name: z.string(),
  surface: z.enum(['tv', 'phone']),
  description: z.string(),
  zones: z.array(ZoneSchema),
  linkedSectionIds: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// ---------------------------------------------------------------------------
// Hammer change-impact plan (stage 1). Sections only — the Phase-4 Hammer
// produces section drafts (TDD §13). Strings kept short by the prompt.
// ---------------------------------------------------------------------------

export const HammerPlanSchema = z.object({
  summary: z.string().default(''),
  sections: z
    .array(
      z.object({
        sectionId: z.string(),
        diff: z.string().default(''),
        rationale: z.string().default(''),
      }),
    )
    .default([]),
});

export type HammerPlan = z.infer<typeof HammerPlanSchema>;

// ---------------------------------------------------------------------------
// Pre-sync contradiction check — disagreements BETWEEN edited source-of-truth
// sections, surfaced before the ripple propagates them (#1936). `id` +
// `sectionTitle` are derived client-side in hammer-flow, so the model only
// returns `topic`/`claims`/`suggestion`.
// ---------------------------------------------------------------------------

export const DesignConflictPlanSchema = z.object({
  conflicts: z
    .array(
      z.object({
        topic: z.string(),
        claims: z
          .array(
            z.object({
              sectionId: z.string(),
              claim: z.string().default(''),
            }),
          )
          .default([]),
        suggestion: z.string().optional(),
      }),
    )
    .default([]),
});

export type DesignConflictPlan = z.infer<typeof DesignConflictPlanSchema>;

// Phased build plan — rendered in the build-handoff review modal.
export const PhasePlanSchema = z.object({
  phases: z
    .array(
      z.object({
        id: z.string().default(''),
        title: z.string().default(''),
        goal: z.string().default(''),
        deliverables: z.array(z.string()).default([]),
        acceptance: z.array(z.string()).default([]),
        estimate: z.string().default(''),
        pauseForReview: z.boolean().default(false),
        pauseGate: z.array(z.string()).default([]),
      }),
    )
    .default([]),
});

export type PhasePlan = z.infer<typeof PhasePlanSchema>;

// Design review questions (adversarial pass).
export const ReviewQuestionsSchema = z.object({
  questions: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        question: z.string(),
        area: z.string().default(''),
        severity: z.enum(['low', 'medium', 'high']).default('medium'),
        why: z.string().default(''),
        recommendation: z.string().default(''),
      }),
    )
    .default([]),
});

export type ReviewQuestions = z.infer<typeof ReviewQuestionsSchema>;

// Wizard follow-up questions (bootstrap a design from a pitch).
export const WizardQuestionsSchema = z.object({
  questions: z
    .array(
      z.object({
        id: z.string(),
        question: z.string(),
        hint: z.string().default(''),
      }),
    )
    .default([]),
});

// ---------------------------------------------------------------------------
// Forgiving AI-JSON parse (§7.6) — replaces Anvil's JSON5/jsonrepair triple
// fallback with a tiny pre-clean + zod validation. Strips ```json fences and
// trailing commas, then validates against the supplied schema.
// ---------------------------------------------------------------------------

export function parseAiJson<T>(text: string, schema: z.ZodType<T>): T {
  let cleaned = text.trim();
  // Strip a leading ```json / ``` fence and trailing ```.
  cleaned = cleaned.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  // If there's prose around the object, slice to the outermost braces.
  const first = cleaned.indexOf('{');
  const last = cleaned.lastIndexOf('}');
  if (first > 0 || last < cleaned.length - 1) {
    if (first !== -1 && last !== -1 && last > first) {
      cleaned = cleaned.slice(first, last + 1);
    }
  }
  // Remove trailing commas before } or ].
  cleaned = cleaned.replace(/,(\s*[}\]])/g, '$1');
  const obj: unknown = JSON.parse(cleaned);
  return schema.parse(obj);
}
